import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import axios from "axios";
import { toast } from "sonner";
import { ChevronRight, ShoppingCart, Lock, Truck, MapPin, Loader2, Zap } from "lucide-react";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || "sb";

export const CheckoutPage = () => {
  const navigate = useNavigate();
  const { items, subtotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [shippingOptions, setShippingOptions] = useState([]);
  const [loadingRates, setLoadingRates] = useState(false);
  const [selectedShipping, setSelectedShipping] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [ratesLoaded, setRatesLoaded] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    street: "",
    city: "",
    state: "",
    zip: "",
    notes: "",
  });

  // Fetch default shipping options on load
  useEffect(() => {
    const fetchDefaultRates = async () => {
      try {
        const response = await axios.get(`${API}/shipping/options`);
        setShippingOptions(response.data);
        if (response.data.length > 0) {
          setSelectedShipping(response.data[0].id);
        }
      } catch (error) {
        console.error("Failed to fetch shipping options:", error);
        setShippingOptions([
          { id: "ground-advantage", name: "USPS Ground Advantage", price: 5.99, days: "2-5 business days" },
          { id: "priority-mail", name: "USPS Priority Mail", price: 9.99, days: "1-3 business days" },
          { id: "priority-mail-express", name: "USPS Priority Mail Express", price: 26.99, days: "1-2 business days" },
        ]);
        setSelectedShipping("ground-advantage");
      }
    };
    fetchDefaultRates();
  }, []);

  // Fetch live rates when ZIP code changes
  const fetchLiveRates = useCallback(async (zip) => {
    if (!zip || zip.length < 5) return;

    setLoadingRates(true);
    try {
      const response = await axios.post(`${API}/shipping/rates`, {
        zip_code: zip,
        items: items.map((item) => ({
          product_id: item.product_id,
          quantity: item.quantity,
        })),
      });
      if (Array.isArray(response.data) && response.data.length > 0) {
        setShippingOptions(response.data);
        setRatesLoaded(true);
        setZipCode(zip);

        // Keep current selection if still available, otherwise select first option
        setSelectedShipping((prev) => {
          const stillAvailable = response.data.find((opt) => opt.id === prev);
          return stillAvailable ? prev : response.data[0].id;
        });

        toast.success("Shipping rates updated for your location!");
      }
    } catch (error) {
      console.error("Failed to fetch live rates:", error);
    } finally {
      setLoadingRates(false);
    }
  }, [items]);

  // Handle ZIP code change with debounce
  useEffect(() => {
    const cleanZip = form.zip.replace(/\D/g, "");
    if (cleanZip.length !== 5) return;
    const timer = setTimeout(() => {
      fetchLiveRates(cleanZip);
    }, 500);
    return () => clearTimeout(timer);
  }, [form.zip, fetchLiveRates]);

  const isDigitalOnly = items.length > 0 && items.every((it) => it.product_type === "digital");
  const currentShipping = shippingOptions.find(s => s.id === selectedShipping) || shippingOptions[0];
  const shippingCost = isDigitalOnly ? 0 : (currentShipping?.price || 0);
  const total = subtotal + shippingCost;

  const validateForm = () => {
    if (isDigitalOnly) {
      if (!form.name || !form.email) {
        toast.error("Please enter your name and email");
        return false;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
        toast.error("Please enter a valid email address");
        return false;
      }
      return true;
    }
    if (!form.name || !form.email || !form.street || !form.city || !form.state || !form.zip) {
      toast.error("Please fill in all required fields");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Please enter a valid email address");
      return false;
    }
    if (form.zip.replace(/\D/g, "").length !== 5) {
      toast.error("Please enter a valid 5-digit ZIP code");
      return false;
    }
    return true;
  };

  const formatAddress = () => {
    if (isDigitalOnly) return "Digital delivery — emailed after purchase";
    return `${form.street}\n${form.city}, ${form.state} ${form.zip}`;
  };

  const createOrder = async () => {
    if (!validateForm()) return null;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/orders`, {
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone,
        shipping_address: formatAddress(),
        shipping_zip: isDigitalOnly ? null : form.zip.replace(/\D/g, "").slice(0, 5),
        shipping_method: isDigitalOnly ? "digital" : selectedShipping,
        items: items.map((item) => ({
          product_id: item.product_id,
          quantity: item.quantity,
        })),
        notes: form.notes,
      });
      setOrderId(response.data.id);
      return response.data.id;
    } catch (error) {
      toast.error("Failed to create order. Please try again.");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handlePayPalApprove = async (data) => {
    try {
      await axios.post(`${API}/orders/${orderId}/capture`, {
        order_id: orderId,
        paypal_order_id: data.orderID,
      });
      
      clearCart();
      toast.success("Payment successful! Thank you for your order.");
      navigate(`/order-confirmation/${orderId}`);
    } catch (error) {
      toast.error("Failed to process payment. Please contact support.");
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center animate-fade-in">
        <div className="text-center">
          <ShoppingCart className="w-16 h-16 mx-auto text-stone-300 mb-4" />
          <h1 className="font-serif text-2xl text-stone-900 mb-4">Your cart is empty</h1>
          <Link
            to="/shop"
            data-testid="continue-shopping-link"
            className="text-deep-moss font-sans hover:underline"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-stone-200">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl py-4">
          <nav className="flex items-center gap-2 text-sm font-sans" data-testid="breadcrumb">
            <Link to="/" className="text-stone-500 hover:text-deep-moss">
              Home
            </Link>
            <ChevronRight className="w-4 h-4 text-stone-400" />
            <Link to="/shop" className="text-stone-500 hover:text-deep-moss">
              Shop
            </Link>
            <ChevronRight className="w-4 h-4 text-stone-400" />
            <span className="text-stone-900">Checkout</span>
          </nav>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-12">
        <h1 className="font-serif text-3xl md:text-4xl font-semibold text-stone-900 mb-8">
          Checkout
        </h1>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Customer Info Form */}
          <div className="space-y-8">
            <div className="bg-white p-6 md:p-8 border border-stone-200">
              <h2 className="font-serif text-xl font-semibold text-stone-900 mb-6">
                {isDigitalOnly ? "Contact Information" : "Shipping Information"}
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block font-sans text-sm text-stone-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="checkout-name"
                    className="w-full input-underline"
                    placeholder="John Doe"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-sans text-sm text-stone-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="checkout-email"
                      className="w-full input-underline"
                      placeholder="john@email.com"
                    />
                  </div>
                  <div>
                    <label className="block font-sans text-sm text-stone-700 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      data-testid="checkout-phone"
                      className="w-full input-underline"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                {isDigitalOnly && (
                  <div className="flex items-center gap-2 text-deep-moss bg-deep-moss/5 px-3 py-3 rounded-lg" data-testid="digital-delivery-note">
                    <Zap className="w-4 h-4 shrink-0" />
                    <span className="font-sans text-sm">Digital order — your download link will be emailed after purchase. No shipping needed.</span>
                  </div>
                )}
                {!isDigitalOnly && (
                <div>
                  <label className="block font-sans text-sm text-stone-700 mb-2">
                    Street Address *
                  </label>
                  <input
                    type="text"
                    value={form.street}
                    onChange={(e) => setForm({ ...form, street: e.target.value })}
                    data-testid="checkout-street"
                    className="w-full input-underline"
                    placeholder="123 Main Street"
                  />
                </div>
                )}

                {!isDigitalOnly && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="col-span-2">
                    <label className="block font-sans text-sm text-stone-700 mb-2">
                      City *
                    </label>
                    <input
                      type="text"
                      value={form.city}
                      onChange={(e) => setForm({ ...form, city: e.target.value })}
                      data-testid="checkout-city"
                      className="w-full input-underline"
                      placeholder="New Orleans"
                    />
                  </div>
                  <div>
                    <label className="block font-sans text-sm text-stone-700 mb-2">
                      State *
                    </label>
                    <input
                      type="text"
                      value={form.state}
                      onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase().slice(0, 2) })}
                      data-testid="checkout-state"
                      className="w-full input-underline"
                      placeholder="LA"
                      maxLength={2}
                    />
                  </div>
                  <div>
                    <label className="block font-sans text-sm text-stone-700 mb-2 flex items-center gap-1">
                      ZIP *
                      {loadingRates && <Loader2 className="w-3 h-3 animate-spin text-deep-moss" />}
                    </label>
                    <input
                      type="text"
                      value={form.zip}
                      onChange={(e) => setForm({ ...form, zip: e.target.value.replace(/\D/g, "").slice(0, 5) })}
                      data-testid="checkout-zip"
                      className="w-full input-underline"
                      placeholder="70458"
                      maxLength={5}
                    />
                  </div>
                </div>
                )}

                {!isDigitalOnly && ratesLoaded && (
                  <div className="flex items-center gap-2 text-green-700 bg-green-50 px-3 py-2 rounded-lg">
                    <MapPin className="w-4 h-4" />
                    <span className="font-sans text-sm">Live shipping rates loaded for ZIP {zipCode}</span>
                  </div>
                )}

                <div>
                  <label className="block font-sans text-sm text-stone-700 mb-2">
                    Order Notes (optional)
                  </label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    data-testid="checkout-notes"
                    rows={2}
                    className="w-full input-underline resize-none"
                    placeholder="Special instructions for your order..."
                  />
                </div>
              </div>
            </div>

            {/* Shipping Options */}
            {!isDigitalOnly && (
            <div className="bg-white p-6 md:p-8 border border-stone-200">
              <h2 className="font-serif text-xl font-semibold text-stone-900 mb-2">
                Shipping Method
              </h2>
              <p className="font-sans text-sm text-stone-500 mb-6 flex items-center gap-2">
                <Truck className="w-4 h-4" />
                {ratesLoaded ? "Live USPS rates for your location" : "Enter ZIP code for live rates"}
              </p>

              <div className="space-y-3">
                {shippingOptions.map((option) => (
                  <label
                    key={option.id}
                    data-testid={`shipping-option-${option.id}`}
                    className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-all ${
                      selectedShipping === option.id
                        ? "border-deep-moss bg-deep-moss/5"
                        : "border-stone-200 hover:border-stone-300"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="shipping"
                        value={option.id}
                        checked={selectedShipping === option.id}
                        onChange={() => setSelectedShipping(option.id)}
                        className="w-4 h-4 text-deep-moss"
                      />
                      <div>
                        <p className="font-sans text-sm font-medium text-stone-900">
                          {option.name}
                        </p>
                        <p className="font-sans text-xs text-stone-500">
                          {option.days}
                        </p>
                      </div>
                    </div>
                    <span className="font-sans text-sm font-semibold text-stone-900">
                      ${option.price.toFixed(2)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            )}

            {/* PayPal Payment */}
            <div className="bg-white p-6 md:p-8 border border-stone-200">
              <h2 className="font-serif text-xl font-semibold text-stone-900 mb-2">
                Payment
              </h2>
              <p className="font-sans text-sm text-stone-500 mb-6 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Secure payment via PayPal
              </p>

              <PayPalScriptProvider
                options={{
                  clientId: PAYPAL_CLIENT_ID,
                  currency: "USD",
                }}
              >
                <PayPalButtons
                  style={{ layout: "vertical", shape: "pill" }}
                  disabled={loading}
                  createOrder={async () => {
                    const newOrderId = await createOrder();
                    if (!newOrderId) throw new Error("Order creation failed");
                    return newOrderId;
                  }}
                  onApprove={handlePayPalApprove}
                  onError={(err) => {
                    console.error("PayPal error:", err);
                    toast.error("Payment failed. Please try again.");
                  }}
                />
              </PayPalScriptProvider>

              <p className="font-sans text-xs text-stone-400 mt-4 text-center">
                By completing your purchase, you agree to our terms and conditions.
              </p>
            </div>
          </div>

          {/* Order Summary */}
          <div>
            <div className="bg-white p-6 md:p-8 border border-stone-200 sticky top-24">
              <h2 className="font-serif text-xl font-semibold text-stone-900 mb-6">
                Order Summary
              </h2>

              <div className="space-y-4 mb-6">
                {items.map((item) => (
                  <div
                    key={item.product_id}
                    className="flex gap-4"
                    data-testid={`checkout-item-${item.product_id}`}
                  >
                    <img
                      src={item.image_url}
                      alt={item.name}
                      className="w-16 h-16 object-cover border border-stone-200"
                    />
                    <div className="flex-1">
                      <h3 className="font-sans text-sm font-medium text-stone-900">
                        {item.name}
                      </h3>
                      <p className="font-sans text-xs text-stone-500">
                        Qty: {item.quantity}
                      </p>
                    </div>
                    <p className="font-sans text-sm font-semibold text-stone-900">
                      ${(item.price * item.quantity).toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>

              <div className="border-t border-stone-200 pt-4 space-y-2">
                <div className="flex justify-between font-sans text-sm">
                  <span className="text-stone-600">Subtotal</span>
                  <span className="text-stone-900">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-sans text-sm">
                  <span className="text-stone-600">
                    {isDigitalOnly ? "Delivery" : `Shipping (${currentShipping?.name || "Standard"})`}
                  </span>
                  <span className="text-stone-900">
                    {isDigitalOnly ? "Free (digital)" : `$${shippingCost.toFixed(2)}`}
                  </span>
                </div>
                <div className="flex justify-between font-sans text-lg font-semibold pt-2 border-t border-stone-200">
                  <span className="text-stone-900">Total</span>
                  <span className="text-deep-moss">${total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
